import React from 'react';
import { Badge, BadgeTone } from '@ui-library';
import {
  EnvironmentRuntime,
  EnvironmentRuntimeStatus,
  EnvironmentStatus,
} from '../../services/projectEnvironment';

type Meta = { label: string; tone: BadgeTone; spinning?: boolean };

const STATUS_META: Record<EnvironmentStatus, Meta> = {
  // Named for what the user should do next, not for what already happened —
  // a created environment has a URL that does not resolve until it is deployed.
  created: { label: 'Not deployed', tone: 'neutral' },
  deploying: { label: 'Deploying', tone: 'brand', spinning: true },
  deploy_failed: { label: 'Deploy failed', tone: 'error' },
  deployed: { label: 'Deployed', tone: 'success' },
};

/** Status pill — one per record status. */
export const StatusBadge: React.FC<{ status: EnvironmentStatus }> = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.deploy_failed;
  return (
    <Badge tone={meta.tone} spinning={meta.spinning}>
      {meta.label}
    </Badge>
  );
};

const RUNTIME_META: Record<EnvironmentRuntimeStatus, Meta | null> = {
  running: { label: 'Running', tone: 'success' },
  stopped: { label: 'Stopped', tone: 'neutral' },
  starting: { label: 'Starting', tone: 'brand', spinning: true },
  stopping: { label: 'Stopping', tone: 'brand', spinning: true },
  partial: { label: 'Partially running', tone: 'warning' },
  error: { label: 'Unhealthy', tone: 'error' },
  // Docker unreachable — a neutral nothing, not an error.
  unknown: null,
};

/** Live container-state pill — Running · Stopped · Starting · … Renders nothing for 'unknown'. */
export const RuntimeBadge: React.FC<{ status: EnvironmentRuntimeStatus }> = ({ status }) => {
  const meta = RUNTIME_META[status];
  if (!meta) return null;
  return (
    <Badge tone={meta.tone} spinning={meta.spinning}>
      {meta.label}
    </Badge>
  );
};

/** The one pill an environment shows.
 *
 * The record status is the default story — 'Deployed' already means built and
 * serving, so 'running' needs no pill of its own. The runtime pill takes over
 * only when a *deployed* environment's containers deviate from that: stopped,
 * starting, stopping, partial or unhealthy.
 *
 * Before it is deployed the runtime pill is suppressed entirely. Such an
 * environment reports 'stopped' because it has no containers, and showing
 * 'Stopped' would imply it could simply be started. */
export const EnvironmentBadge: React.FC<{
  status: EnvironmentStatus;
  runtime?: EnvironmentRuntime;
}> = ({ status, runtime }) => {
  if (
    status === 'deployed' &&
    runtime &&
    runtime.status !== 'running' &&
    RUNTIME_META[runtime.status]
  ) {
    return <RuntimeBadge status={runtime.status} />;
  }
  return <StatusBadge status={status} />;
};

export default StatusBadge;
